import { LocalizedText } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import {
    SalesforceCaseCategory,
    SalesforceCasePriority,
    SalesforceCaseReason,
    SalesforceCreateCaseParameters,
} from '@mews-commander/data-access';
import { Option } from '@mews-commander/legacy-data-structures';
import { EXTENDED_TICKET_DESCRIPTION_ROW_COUNT } from '../constants';

export const SalesforceKeys = {
    TITLE: 'title',
    REASON: 'reason',
    COLLABORATORS: 'collaborators',
    PRIORITY: 'priority',
    DESCRIPTION: 'description',
    CATEGORY: 'category',
} satisfies Record<string, keyof SalesforceCreateCaseParameters>;

export const collaboratorsProps = {
    name: SalesforceKeys.COLLABORATORS,
    label: <LocalizedText textKey={TextKey.Collaborators} />,
    helper: <LocalizedText textKey={TextKey.AddEmailsOfColleagues} />,
};

export const reasonProps = {
    label: <LocalizedText textKey={TextKey.HowWouldYouDescribeYourRequest} />,
    name: SalesforceKeys.REASON,
    required: true,
    options: [
        Option({
            value: SalesforceCaseReason.HowTo,
            label: <LocalizedText textKey={TextKey.Question} />,
        }),
        Option({
            value: SalesforceCaseReason.TechnicalIssue,
            label: <LocalizedText textKey={TextKey.TechnicalIssue} />,
        }),
    ],
};

export const priorityProps = {
    label: <LocalizedText textKey={TextKey.IsYourDailyOperationAffected} />,
    name: SalesforceKeys.PRIORITY,
    required: true,
    options: [
        Option({
            value: SalesforceCasePriority.Low,
            label: <LocalizedText textKey={TextKey.SalesforceCasePriorityLow} />,
        }),
        Option({
            value: SalesforceCasePriority.High,
            label: <LocalizedText textKey={TextKey.SalesforceCasePriorityHigh} />,
        }),
        Option({
            value: SalesforceCasePriority.Critical,
            label: <LocalizedText textKey={TextKey.SalesforceCasePriorityCritical} />,
        }),
    ],
};

export const descriptionProps = {
    label: <LocalizedText textKey={TextKey.DescribeYourRequest} />,
    name: SalesforceKeys.DESCRIPTION,
    required: true,
    placeholder: <LocalizedText textKey={TextKey.PleaseAddReservationAndBillNumber} />,
    helper: <LocalizedText textKey={TextKey.ProvideAsManyDetailsAsPossible} />,
    rows: EXTENDED_TICKET_DESCRIPTION_ROW_COUNT,
};

const categoryOptions = [
    {
        groupLabel: <LocalizedText textKey={TextKey.OperationPlural} />,
        groupOptions: [
            Option({
                value: SalesforceCaseCategory.OperationsReservationManagement,
                label: <LocalizedText textKey={TextKey.AccountNoteCustomerClassificationsReservations} />,
            }),
            Option({
                value: SalesforceCaseCategory.OperationsRateManagement,
                label: <LocalizedText textKey={TextKey.RateManagement} />,
            }),
            Option({
                value: SalesforceCaseCategory.OperationsSettings,
                label: <LocalizedText textKey={TextKey.Settings} />,
            }),
            Option({
                value: SalesforceCaseCategory.BusinessIntelligenceReports,
                label: <LocalizedText textKey={TextKey.ReportPlural} />,
            }),
        ],
    },
    {
        groupLabel: <LocalizedText textKey={TextKey.FinanceAndPayments} />,
        groupOptions: [
            Option({
                value: SalesforceCaseCategory.OperationsBilling,
                label: <LocalizedText textKey={TextKey.Billing} />,
            }),
            Option({
                value: SalesforceCaseCategory.PaymentsRefunds,
                label: <LocalizedText textKey={TextKey.RefundPlural} />,
            }),
            Option({
                value: SalesforceCaseCategory.PaymentsTerminal,
                label: <LocalizedText textKey={TextKey.Terminal} />,
            }),
        ],
    },
    {
        groupLabel: <LocalizedText textKey={TextKey.MarketplaceAndIntegration} />,
        groupOptions: [
            Option({
                value: SalesforceCaseCategory.MarketplaceChannelManager,
                label: <LocalizedText textKey={TextKey.ChannelManagerIntegrationPlural} />,
            }),
            Option({
                value: SalesforceCaseCategory.MarketplaceRevenueManagement,
                label: <LocalizedText textKey={TextKey.RevenueManagementIntegrations} />,
            }),
        ],
    },
    {
        groupLabel: <LocalizedText textKey={TextKey.Navigator} />,
        groupOptions: [
            Option({
                value: SalesforceCaseCategory.GuestJourneyKiosk,
                label: <LocalizedText textKey={TextKey.Kiosk} />,
            }),
            Option({
                value: SalesforceCaseCategory.GuestJourneyBookingEngine,
                label: <LocalizedText textKey={TextKey.BookingEngine} />,
            }),
        ],
    },
    {
        groupLabel: <LocalizedText textKey={TextKey.Other} />,
        groupOptions: [
            Option({
                value: SalesforceCaseCategory.ApiDevelopers,
                label: <LocalizedText textKey={TextKey.MewsApiDevelopers} />,
            }),
            Option({
                value: SalesforceCaseCategory.Restaurants,
                label: <LocalizedText textKey={TextKey.MewsPosBizzon} />,
            }),
            Option({
                value: SalesforceCaseCategory.MySubscriptionInvoicePayments,
                label: <LocalizedText textKey={TextKey.PlatformAndSubscription} />,
            }),
            Option({
                value: SalesforceCaseCategory.Other,
                label: <LocalizedText textKey={TextKey.Other} />,
            }),
        ],
    },
];

export const categoryProps = {
    label: <LocalizedText textKey={TextKey.WhatIsYourRequestRelatedTo} />,
    name: SalesforceKeys.CATEGORY,
    required: true,
    placeholder: <LocalizedText textKey={TextKey.PleaseSelect} />,
    options: categoryOptions,
};
